<template>
  <v-container fluid class="pcal">
    <!-- ส่วนหัว -->
    <v-card flat class="pcal-head mb-4">
      <v-card-text class="d-flex flex-wrap align-center pa-4">
        <div class="pcal-head-icon mr-3"><v-icon color="white">mdi-calendar-multiselect</v-icon></div>
        <div class="mr-auto">
          <div class="pcal-title">ปฏิทินกำหนดการรวมของระบบ</div>
          <div class="caption grey--text text--darken-1">
            รวมทุกกำหนดการจาก 6 ตาราง พร้อมคำอธิบายว่าแต่ละรายการคุมกิจกรรมใด และตรวจความถูกต้องอัตโนมัติ
          </div>
        </div>
        <span v-if="data.today" class="caption grey--text mr-2">ข้อมูล ณ {{ thaiDate(data.today) }}</span>
        <v-btn icon :loading="loading" title="โหลดใหม่" @click="load"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-card-text>
    </v-card>

    <v-alert v-if="loaded && !data.registry_available" type="warning" text border="left" class="mb-4">
      ยังไม่ได้ติดตั้งทะเบียนกำหนดการ (<b>MIGRATION_period_registry.sql</b>) — ดูข้อมูลได้ แต่ยังแก้ไขจากหน้านี้ไม่ได้
      และรายการจะยังไม่มีคำอธิบายกำกับ
    </v-alert>
    <v-alert v-if="error" type="error" text dense class="mb-4">{{ error }}</v-alert>

    <!-- ตัวเลขสรุป -->
    <v-row dense class="mb-2">
      <v-col v-for="k in kpis" :key="k.key" cols="6" sm="4" md="2">
        <div
          class="pcal-kpi"
          :class="{ 'pcal-kpi--active': quick === k.key }"
          :style="{ borderLeftColor: k.hex }"
          role="button"
          @click="quick = quick === k.key ? null : k.key"
        >
          <div class="pcal-kpi-value" :style="{ color: k.hex }">{{ k.value }}</div>
          <div class="pcal-kpi-label">{{ k.label }}</div>
        </div>
      </v-col>
      <v-col cols="12" md="2" class="d-flex align-center">
        <v-btn
          block
          depressed
          color="red darken-1"
          dark
          :disabled="!overdueRows.length || !data.registry_available"
          :loading="closing"
          @click="closeOverdue()"
        >
          <v-icon left>mdi-calendar-remove</v-icon>ปิดที่เลยกำหนด ({{ overdueRows.length }})
        </v-btn>
      </v-col>
    </v-row>

    <!-- ตัวกรอง -->
    <v-card flat class="pcal-filter mb-3">
      <v-card-text class="pa-3">
        <v-row dense align="center">
          <v-col cols="12" md="4">
            <v-text-field v-model.trim="search" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify"
              label="ค้นหา ชื่อกิจกรรม / รหัส / ประเภท / หน้าที่ใช้" />
          </v-col>
          <v-col cols="6" md="2">
            <v-select v-model="missionFilter" :items="missionItems" dense outlined hide-details label="ภารกิจ" clearable />
          </v-col>
          <v-col cols="6" md="2">
            <v-select v-model="actorFilter" :items="actorItems" dense outlined hide-details label="ผู้ดำเนินการ" clearable />
          </v-col>
          <v-col cols="6" md="2">
            <v-switch v-model="showUnused" dense hide-details inset label="แสดงรายการไม่ได้ใช้" class="mt-0" />
          </v-col>
          <v-col cols="6" md="2">
            <v-switch v-model="onlyIssues" dense hide-details inset label="เฉพาะที่มีปัญหา" class="mt-0" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-tabs v-model="tab" class="mb-3" show-arrows>
      <v-tab><v-icon left small>mdi-chart-gantt</v-icon>ปฏิทิน</v-tab>
      <v-tab><v-icon left small>mdi-format-list-bulleted-square</v-icon>รายการทั้งหมด</v-tab>
      <v-tab>
        <v-icon left small>mdi-stethoscope</v-icon>ตรวจสอบข้อมูล
        <v-chip x-small color="red darken-1" dark class="ml-2">{{ issueRows.length }}</v-chip>
      </v-tab>
    </v-tabs>

    <v-skeleton-loader v-if="loading && !loaded" type="table" />

    <v-tabs-items v-else v-model="tab" class="pcal-tabitems">
      <!-- ============ ปฏิทิน (Gantt) ============ -->
      <v-tab-item>
        <v-card flat class="pcal-card">
          <div class="d-flex flex-wrap align-center pa-3">
            <v-btn icon small @click="fiscalYear--"><v-icon>mdi-chevron-left</v-icon></v-btn>
            <span class="font-weight-bold mx-2">ปีงบประมาณ {{ fiscalYear }}</span>
            <v-btn icon small @click="fiscalYear++"><v-icon>mdi-chevron-right</v-icon></v-btn>
            <v-btn small text color="primary" @click="fiscalYear = currentFiscalYear">ปีปัจจุบัน</v-btn>
            <v-spacer />
            <span v-for="(m, s) in legendStates" :key="s" class="pcal-legend mr-3">
              <span class="pcal-legend-dot" :style="{ background: m.hex }" />{{ m.text }}
            </span>
          </div>
          <div class="pcal-gantt">
            <div class="pcal-g-row pcal-g-headrow">
              <div class="pcal-g-label" />
              <div class="pcal-g-track">
                <div v-for="(m, i) in ganttMonths" :key="i" class="pcal-g-month" :style="{ left: m.left + '%', width: m.width + '%' }">
                  {{ m.label }}
                </div>
                <div v-if="todayPos !== null" class="pcal-g-today" :style="{ left: todayPos + '%' }" />
              </div>
            </div>
            <template v-for="g in ganttGroups">
              <div :key="'g' + g.mission" class="pcal-g-group">{{ missionLabel(g.mission) }} ({{ g.rows.length }})</div>
              <div v-for="r in g.rows" :key="r.key" class="pcal-g-row" @click="openEdit(r)">
                <div class="pcal-g-label" :title="r.title">
                  <v-icon x-small :color="actorMeta(r.actor).color" class="mr-1">{{ actorMeta(r.actor).icon }}</v-icon>
                  {{ r.title }}
                </div>
                <div class="pcal-g-track">
                  <div v-for="(m, i) in ganttMonths" :key="i" class="pcal-g-grid" :style="{ left: m.left + '%' }" />
                  <div v-if="todayPos !== null" class="pcal-g-today" :style="{ left: todayPos + '%' }" />
                  <div
                    v-if="r.bar"
                    class="pcal-g-bar"
                    :style="{ left: r.bar.left + '%', width: r.bar.width + '%', background: stateMeta(r.state).hex }"
                    :title="`${r.title}\n${thaiDate(r.effective_start)} – ${thaiDate(r.effective_end)} · ${stateMeta(r.state).text}`"
                  />
                </div>
              </div>
            </template>
            <div v-if="!ganttGroups.length" class="pa-6 text-center grey--text">
              ไม่มีกำหนดการที่อยู่ในปีงบประมาณ {{ fiscalYear }} ตามตัวกรองนี้
            </div>
          </div>
        </v-card>
      </v-tab-item>

      <!-- ============ รายการทั้งหมด ============ -->
      <v-tab-item>
        <v-card flat class="pcal-card">
          <v-data-table
            :headers="headers"
            :items="filteredRows"
            item-key="key"
            group-by="mission"
            :items-per-page="-1"
            hide-default-footer
            dense
            class="pcal-table"
            @click:row="openEdit"
          >
            <template v-slot:group.header="{ items, isOpen, toggle }">
              <td :colspan="headers.length" class="pcal-group-head">
                <v-btn icon x-small @click="toggle"><v-icon>{{ isOpen ? "mdi-minus" : "mdi-plus" }}</v-icon></v-btn>
                {{ missionLabel(items[0].mission) }} ({{ items.length }})
              </td>
            </template>
            <template v-slot:item.title="{ item }">
              <div class="font-weight-medium" :class="{ 'grey--text': item.reg_status !== 'active' }">{{ item.title }}</div>
              <div v-if="item.description" class="caption grey--text text--darken-1 pcal-desc">{{ item.description }}</div>
            </template>
            <template v-slot:item.key="{ item }">
              <div class="caption">{{ item.table_label }}</div>
              <code class="pcal-code">{{ item.key }}</code>
              <div v-if="item.type" class="caption grey--text">{{ item.type }}</div>
            </template>
            <template v-slot:item.actor="{ item }">
              <v-chip x-small outlined :color="actorMeta(item.actor).color">
                <v-icon left x-small>{{ actorMeta(item.actor).icon }}</v-icon>{{ actorMeta(item.actor).text }}
              </v-chip>
            </template>
            <template v-slot:item.round="{ item }">
              <span v-if="item.year">{{ item.times }}/{{ item.year }}</span>
              <div v-if="item.active_subround" class="caption">งวดที่ {{ item.active_subround }}</div>
            </template>
            <template v-slot:item.range="{ item }">
              <span v-if="item.effective_start">{{ thaiDate(item.effective_start) }} – {{ thaiDate(item.effective_end) }}</span>
              <div v-if="item.attach_stop" class="caption grey--text">ปิดแนบไฟล์ {{ thaiDate(item.attach_stop) }}</div>
            </template>
            <template v-slot:item.state="{ item }">
              <v-chip x-small :color="stateMeta(item.state).color" dark>{{ stateText(item) }}</v-chip>
              <div class="caption mt-1">
                <v-icon x-small :color="item.enabled ? 'success' : 'grey'">mdi-power</v-icon> เปิดระบบ
                <v-icon x-small :color="item.sw2 ? 'success' : 'grey'" class="ml-1">mdi-paperclip</v-icon> {{ item.sw2_label }}
              </div>
            </template>
            <template v-slot:item.issues="{ item }">
              <v-icon
                v-for="(i, n) in item.issues.filter(x => x.level !== 'info')"
                :key="n"
                small
                :color="levelColor(i.level)"
                :title="i.text"
              >mdi-alert-circle</v-icon>
              <v-icon v-if="item.issues.some(x => x.level === 'info')" small color="blue-grey lighten-1"
                :title="item.issues.filter(x => x.level === 'info').map(x => x.text).join('\n')">mdi-information</v-icon>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small title="แก้ไข" @click.stop="openEdit(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-btn v-if="item.admin_route" icon small title="เปิดหน้าตั้งค่าเดิม" :to="item.admin_route" @click.stop>
                <v-icon small>mdi-open-in-app</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ============ ตรวจสอบข้อมูล ============ -->
      <v-tab-item>
        <v-card flat class="pcal-card pa-3">
          <v-alert v-if="overdueRows.length" type="error" text dense border="left">
            มี <b>{{ overdueRows.length }}</b> กำหนดการที่เลยวันสิ้นสุดแต่สวิตช์ยังเปิด — ผู้ใช้ยังบันทึก/แก้ไขข้อมูลได้
            แสดงว่าระบบปิดอัตโนมัติ (cron) ไม่ได้ทำงาน · ปิดทั้งหมดได้จากปุ่ม “ปิดที่เลยกำหนด” ด้านบน
          </v-alert>
          <div v-if="!issueRows.length" class="pa-6 text-center">
            <v-icon color="success" large>mdi-check-decagram</v-icon>
            <div class="mt-2">ไม่พบปัญหาในข้อมูลกำหนดการตามตัวกรองนี้</div>
          </div>
          <div v-for="r in issueRows" :key="r.key" class="pcal-issue">
            <div class="d-flex flex-wrap align-center">
              <span class="font-weight-medium mr-2">{{ r.title }}</span>
              <code class="pcal-code mr-2">{{ r.key }}</code>
              <v-chip x-small outlined class="mr-2">{{ missionLabel(r.mission) }}</v-chip>
              <v-spacer />
              <v-btn
                v-if="r.state === 'overdue_open'"
                x-small
                depressed
                color="red darken-1"
                dark
                class="mr-1"
                :loading="closing"
                @click="closeOverdue([r.key])"
              >ปิดกำหนดการ</v-btn>
              <v-btn x-small outlined color="primary" @click="openEdit(r)">แก้ไข</v-btn>
            </div>
            <div v-for="(i, n) in r.issues.filter(x => x.level !== 'info')" :key="n" class="pcal-issue-line">
              <v-icon x-small :color="levelColor(i.level)" class="mr-1">mdi-alert-circle</v-icon>{{ i.text }}
            </div>
          </div>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- ============ แก้ไข ============ -->
    <v-dialog v-model="editOpen" max-width="860" scrollable>
      <v-card v-if="edit" class="pcal-edit">
        <v-card-title class="pcal-edit-head">
          <div>
            <div class="pcal-edit-title">{{ edit.title }}</div>
            <div class="caption">{{ edit.table_label }} · <code class="pcal-code pcal-code--dark">{{ edit.key }}</code>
              <span v-if="edit.type"> · ประเภท {{ edit.type }}</span></div>
          </div>
          <v-spacer />
          <v-btn icon dark @click="editOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-tabs v-model="editTab" height="40">
          <v-tab :disabled="edit.missing">กำหนดการ</v-tab>
          <v-tab>ทะเบียน / คำอธิบาย</v-tab>
          <v-tab v-if="edit.subrounds.length">งวดย่อย ({{ edit.subrounds.length }})</v-tab>
          <v-tab @click="loadHistory">ประวัติการแก้ไข</v-tab>
        </v-tabs>

        <v-card-text class="pt-4 pcal-edit-body">
          <div v-for="(i, n) in edit.issues" :key="n" class="pcal-issue-line mb-1">
            <v-icon x-small :color="levelColor(i.level)" class="mr-1">
              {{ i.level === "info" ? "mdi-information" : "mdi-alert-circle" }}
            </v-icon>{{ i.text }}
          </div>

          <v-tabs-items v-model="editTab" class="mt-3">
            <!-- กำหนดการ -->
            <v-tab-item>
              <v-row dense>
                <v-col cols="6" md="3"><v-text-field v-model="pf.year" label="ปี (พ.ศ.)" dense outlined /></v-col>
                <v-col cols="6" md="3"><v-text-field v-model.number="pf.times" type="number" label="รอบ/ครั้งที่" dense outlined /></v-col>
                <v-col cols="6" md="3"><v-text-field v-model="pf.start" type="date" label="วันเริ่ม" dense outlined /></v-col>
                <v-col cols="6" md="3"><v-text-field v-model="pf.stop" type="date" label="วันสิ้นสุด" dense outlined /></v-col>
                <v-col v-if="edit.has_attach" cols="12" md="4">
                  <v-text-field v-model="pf.attach_stop" type="date" label="วันปิดแนบไฟล์ (ว่างได้)" dense outlined clearable
                    hint="ยังแนบไฟล์ได้ถึงวันนี้ แม้เลยวันสิ้นสุดแล้ว" persistent-hint />
                </v-col>
                <v-col cols="6" md="4"><v-switch v-model="pf.enabled" inset label="เปิดระบบ" color="success" /></v-col>
                <v-col cols="6" md="4"><v-switch v-model="pf.sw2" inset :label="edit.sw2_label" color="success" /></v-col>
                <v-col v-if="edit.has_comment" cols="12">
                  <v-text-field v-model="pf.comment" label="หมายเหตุในตารางเดิม (comment)" dense outlined counter="250" />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="pf.reason" label="เหตุผลการแก้ไข (บันทึกในประวัติ)" dense outlined />
                </v-col>
              </v-row>
              <v-alert v-if="pf.enabled && pfEndPassed" type="warning" text dense>
                วันสิ้นสุดผ่านไปแล้ว แต่ตั้งให้เปิดระบบ — ผู้ใช้จะยังทำรายการได้และหน้าแจ้งเตือนจะไม่แสดงกำหนดการนี้
              </v-alert>
              <div v-if="edit.extra" class="caption grey--text">
                ข้อมูลเฉพาะตาราง (แก้ที่หน้าตั้งค่าเดิม):
                <span v-for="(v, k) in edit.extra" :key="k" class="mr-3">{{ k }}: {{ v === null ? "-" : v }}</span>
              </div>
              <div class="d-flex mt-3">
                <v-btn v-if="edit.admin_route" text color="primary" :to="edit.admin_route">
                  <v-icon left small>mdi-open-in-app</v-icon>หน้าตั้งค่าเดิม
                </v-btn>
                <v-spacer />
                <v-btn color="primary" depressed :loading="saving" :disabled="!data.registry_available" @click="savePeriod">
                  <v-icon left>mdi-content-save</v-icon>บันทึกกำหนดการ
                </v-btn>
              </div>
            </v-tab-item>

            <!-- ทะเบียน -->
            <v-tab-item>
              <v-row dense>
                <v-col cols="12"><v-text-field v-model="mf.title" label="ชื่อกิจกรรม *" dense outlined counter="150" /></v-col>
                <v-col cols="12">
                  <v-textarea v-model="mf.description" label="คำอธิบาย: เปิดแล้วใครทำอะไรได้ / เงื่อนไขพิเศษ" rows="3" auto-grow dense outlined />
                </v-col>
                <v-col cols="12" md="4"><v-select v-model="mf.mission" :items="missionItems" label="ภารกิจ" dense outlined /></v-col>
                <v-col cols="12" md="4"><v-select v-model="mf.actor" :items="actorItems" label="ผู้ได้รับสิทธิ์เมื่อเปิด" dense outlined /></v-col>
                <v-col cols="12" md="4"><v-select v-model="mf.status" :items="statusItems" label="สถานะการใช้งาน" dense outlined /></v-col>
                <v-col cols="12" md="6"><v-text-field v-model="mf.admin_route" label="หน้าตั้งค่า (เช่น /admin/timelines/transfer_teachers)" dense outlined /></v-col>
                <v-col cols="12" md="6"><v-text-field v-model="mf.user_pages" label="หน้าที่ใช้กำหนดการนี้ (ผู้ใช้)" dense outlined /></v-col>
                <v-col cols="12" md="9"><v-text-field v-model="mf.known_issue" label="ปัญหาที่ทราบ / ข้อควรระวัง" dense outlined /></v-col>
                <v-col cols="12" md="3"><v-text-field v-model.number="mf.sort_order" type="number" label="ลำดับแสดง" dense outlined /></v-col>
              </v-row>
              <div class="d-flex">
                <v-spacer />
                <v-btn color="primary" depressed :loading="saving" :disabled="!data.registry_available" @click="saveMeta">
                  <v-icon left>mdi-content-save</v-icon>บันทึกทะเบียน
                </v-btn>
              </div>
            </v-tab-item>

            <!-- งวดย่อย -->
            <v-tab-item v-if="edit.subrounds.length">
              <div class="caption grey--text mb-2">
                งวดที่เปิดอยู่และยังไม่สิ้นสุด จะใช้เป็นช่วงวันที่จริงของกำหนดการนี้ (หน้าแจ้งเตือนสถานศึกษาแสดงตามงวด)
              </div>
              <div v-for="s in sf" :key="s.pcr_id" class="pcal-sub">
                <v-row dense align="center">
                  <v-col cols="12" md="2">
                    <b>งวดที่ {{ s.no }}</b>
                    <div class="caption">เดือน {{ s.months }}</div>
                  </v-col>
                  <v-col cols="6" md="3"><v-text-field v-model="s.start" type="date" label="เริ่ม" dense outlined hide-details /></v-col>
                  <v-col cols="6" md="3"><v-text-field v-model="s.stop" type="date" label="สิ้นสุด" dense outlined hide-details /></v-col>
                  <v-col cols="6" md="2"><v-switch v-model="s.enabled" inset dense hide-details label="เปิด" class="mt-0" /></v-col>
                  <v-col cols="6" md="2" class="text-right">
                    <v-btn small depressed color="primary" :loading="saving" :disabled="!data.registry_available" @click="saveSub(s)">บันทึก</v-btn>
                  </v-col>
                  <v-col cols="12"><v-text-field v-model="s.comment" label="หมายเหตุ" dense outlined hide-details /></v-col>
                </v-row>
              </div>
            </v-tab-item>

            <!-- ประวัติ -->
            <v-tab-item>
              <v-skeleton-loader v-if="historyLoading" type="list-item-two-line@3" />
              <div v-else-if="!history.length" class="grey--text pa-4">ยังไม่มีประวัติการแก้ไขจากหน้านี้</div>
              <div v-for="h in history" :key="h.id" class="pcal-hist">
                <div class="d-flex flex-wrap">
                  <v-chip x-small label class="mr-2">{{ actionText(h.action) }}</v-chip>
                  <span class="caption">{{ h.created_at }} · {{ h.user_firstname || h.changed_by }} {{ h.user_lastname || "" }}</span>
                  <span v-if="h.source_table === 'period_college_reporting'" class="caption ml-2">(งวดย่อย #{{ h.source_id }})</span>
                </div>
                <div v-if="h.reason && h.source_table !== 'period_registry'" class="caption">เหตุผล: {{ h.reason }}</div>
                <div v-for="(c, n) in diff(h)" :key="n" class="caption pcal-diff">
                  {{ c.field }}: <span class="red--text text--darken-1">{{ c.before }}</span> → <span class="green--text text--darken-2">{{ c.after }}</span>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="3500">{{ snack.text }}</v-snackbar>
  </v-container>
</template>

<script>
// ปฏิทินกำหนดการรวม — ข้อมูลจาก admin_period_calendar.php (รวม 6 ตาราง + ทะเบียน period_registry)
// ทุกการแก้ไขบันทึกประวัติฝั่ง backend; หน้านี้ไม่ลบแถวกำหนดการ (เลิกใช้ให้ตั้งสถานะในทะเบียน)
const STATE_META = {
  open: { text: "เปิดอยู่", color: "green darken-1", hex: "#43a047" },
  closing_soon: { text: "ใกล้ปิด", color: "amber darken-3", hex: "#ff8f00" },
  early_open: { text: "เปิดก่อนวันเริ่ม", color: "blue darken-1", hex: "#1e88e5" },
  overdue_open: { text: "เลยกำหนดแต่ยังเปิด", color: "red darken-1", hex: "#e53935" },
  should_open: { text: "ถึงกำหนดแต่ยังปิด", color: "orange darken-2", hex: "#f57c00" },
  upcoming: { text: "กำลังจะเปิด", color: "indigo lighten-1", hex: "#5c6bc0" },
  scheduled: { text: "ตั้งไว้ล่วงหน้า", color: "indigo lighten-3", hex: "#9fa8da" },
  closed: { text: "ปิดแล้ว", color: "grey", hex: "#b0bec5" },
  missing: { text: "ไม่มีในฐานข้อมูล", color: "black", hex: "#424242" }
};
const ACTOR_META = {
  person: { text: "บุคลากร/ผู้สมัคร", color: "orange darken-2", icon: "mdi-account" },
  college: { text: "สถานศึกษา", color: "teal", icon: "mdi-school" },
  vecpro: { text: "สอจ./สถาบัน", color: "deep-purple", icon: "mdi-office-building" },
  central: { text: "ส่วนกลาง", color: "blue-grey", icon: "mdi-shield-account" }
};
const MISSION_LABEL = {
  admin: "ข้อมูลพื้นฐาน/ผู้ใช้งาน",
  manpower: "งานอัตรากำลัง",
  move_teach: "ย้ายข้าราชการครู",
  move_manage: "ย้ายผู้บริหาร",
  govem: "พนักงานราชการ",
  assess_sed: "คัดเลือกผู้บริหาร/ศน.",
  assist_teach: "ครูผู้ช่วย"
};
const MISSION_ORDER = ["move_teach", "move_manage", "govem", "manpower", "assess_sed", "assist_teach", "admin"];
const TH_MONTH = ["ต.ค.", "พ.ย.", "ธ.ค.", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย."];
const DAY = 86400000;

function parseDate(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ""));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

export default {
  name: "PeriodCalendar",

  data() {
    const today = new Date();
    // ปีงบประมาณ พ.ศ.: ต.ค. ขึ้นปีงบใหม่
    const fy = today.getFullYear() + 543 + (today.getMonth() >= 9 ? 1 : 0);
    return {
      ApiKey: "HRvec2021",
      loading: false,
      loaded: false,
      error: "",
      data: { rows: [], summary: {}, missions: [], registry_available: false },
      tab: 0,
      quick: null,
      search: "",
      missionFilter: null,
      actorFilter: null,
      showUnused: false,
      onlyIssues: false,
      currentFiscalYear: fy,
      fiscalYear: fy,
      closing: false,
      editOpen: false,
      editTab: 0,
      edit: null,
      pf: {},
      mf: {},
      sf: [],
      saving: false,
      history: [],
      historyLoading: false,
      snack: { show: false, text: "", color: "success" },
      headers: [
        { text: "กิจกรรม", value: "title", width: "34%" },
        { text: "ตาราง/รหัส", value: "key" },
        { text: "ผู้ดำเนินการ", value: "actor" },
        { text: "รอบ/ปี", value: "round", sortable: false },
        { text: "ช่วงวันที่", value: "range", sortable: false },
        { text: "สถานะ", value: "state" },
        { text: "ปัญหา", value: "issues", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" }
      ],
      statusItems: [
        { text: "ใช้งาน", value: "active" },
        { text: "ไม่มีหน้าใดใช้ (unused)", value: "unused" },
        { text: "เลิกใช้แล้ว (deprecated)", value: "deprecated" }
      ]
    };
  },

  computed: {
    missionItems() {
      return (this.data.missions || []).map(m => ({ text: m.label, value: m.key }));
    },
    actorItems() {
      return Object.entries(ACTOR_META).map(([k, v]) => ({ text: v.text, value: k }));
    },
    legendStates() {
      const keys = ["open", "closing_soon", "overdue_open", "upcoming", "closed"];
      return keys.reduce((o, k) => ({ ...o, [k]: STATE_META[k] }), {});
    },
    kpis() {
      const s = this.data.summary || {};
      return [
        { key: "open", label: "เปิดอยู่", value: s.open || 0, hex: "#43a047" },
        { key: "overdue", label: "เลยกำหนดแต่ยังเปิด", value: s.overdue_open || 0, hex: "#e53935" },
        { key: "upcoming", label: "กำลังจะเปิด (45 วัน)", value: s.upcoming || 0, hex: "#5c6bc0" },
        { key: "issues", label: "ปัญหาข้อมูล", value: (s.error || 0) + (s.warning || 0), hex: "#ef6c00" },
        { key: "unused", label: "ไม่ได้ใช้/เลิกใช้", value: s.unused || 0, hex: "#78909c" }
      ];
    },
    overdueRows() {
      return this.data.rows.filter(r => r.state === "overdue_open");
    },
    filteredRows() {
      const words = (this.search || "").toLowerCase().split(/\s+/).filter(Boolean);
      return this.data.rows.filter(r => {
        if (!this.showUnused && r.reg_status !== "active" && this.quick !== "unused") return false;
        if (this.missionFilter && r.mission !== this.missionFilter) return false;
        if (this.actorFilter && r.actor !== this.actorFilter) return false;
        if (this.onlyIssues && !this.hasProblem(r)) return false;
        if (this.quick === "open" && !["open", "closing_soon", "early_open"].includes(r.state)) return false;
        if (this.quick === "overdue" && r.state !== "overdue_open") return false;
        if (this.quick === "upcoming" && r.state !== "upcoming") return false;
        if (this.quick === "issues" && !this.hasProblem(r)) return false;
        if (this.quick === "unused" && r.reg_status === "active") return false;
        if (words.length) {
          const hay = [r.title, r.description, r.key, r.type, r.comment, r.user_pages, r.admin_route]
            .join(" ")
            .toLowerCase();
          if (!words.every(w => hay.includes(w))) return false;
        }
        return true;
      });
    },
    issueRows() {
      const rank = r => (r.issues.some(i => i.level === "error") ? 0 : 1);
      return this.filteredRows.filter(r => this.hasProblem(r)).sort((a, b) => rank(a) - rank(b));
    },
    // ---- Gantt ----
    fyStart() {
      return new Date(this.fiscalYear - 544, 9, 1); // 1 ต.ค. ของปี ค.ศ. ก่อนหน้า
    },
    fyEnd() {
      return new Date(this.fiscalYear - 543, 8, 30);
    },
    fyDays() {
      return Math.round((this.fyEnd - this.fyStart) / DAY) + 1;
    },
    ganttMonths() {
      const out = [];
      for (let i = 0; i < 12; i++) {
        const a = new Date(this.fyStart.getFullYear(), this.fyStart.getMonth() + i, 1);
        const b = new Date(this.fyStart.getFullYear(), this.fyStart.getMonth() + i + 1, 1);
        out.push({
          label: TH_MONTH[i],
          left: ((a - this.fyStart) / DAY / this.fyDays) * 100,
          width: ((b - a) / DAY / this.fyDays) * 100
        });
      }
      return out;
    },
    todayPos() {
      const t = parseDate(this.data.today);
      if (!t || t < this.fyStart || t > this.fyEnd) return null;
      return ((t - this.fyStart) / DAY / this.fyDays) * 100;
    },
    ganttGroups() {
      const rows = this.filteredRows
        .map(r => ({ ...r, bar: this.barOf(r) }))
        .filter(r => r.bar);
      return MISSION_ORDER.map(m => ({ mission: m, rows: rows.filter(r => r.mission === m) })).filter(g => g.rows.length);
    },
    // แท็บประวัติเลื่อนตำแหน่งเมื่อมี/ไม่มีแท็บงวดย่อย
    historyTab() {
      return this.edit && this.edit.subrounds.length ? 3 : 2;
    },
    pfEndPassed() {
      const today = parseDate(this.data.today);
      const ends = [parseDate(this.pf.stop), parseDate(this.pf.attach_stop)].filter(Boolean);
      return today && ends.length && Math.max(...ends) < today;
    }
  },

  mounted() {
    this.load();
  },

  methods: {
    async api(payload) {
      const res = await this.$http.post("admin_period_calendar.php", { ApiKey: this.ApiKey, ...payload });
      return res.data || {};
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const d = await this.api({ action: "list" });
        if (d.status) {
          this.data = d;
          this.loaded = true;
          if (this.edit) {
            const fresh = d.rows.find(r => r.key === this.edit.key);
            if (fresh) this.edit = fresh;
          }
        } else {
          this.error = d.message || "โหลดข้อมูลกำหนดการไม่สำเร็จ";
        }
      } catch (e) {
        this.error = "โหลดข้อมูลกำหนดการไม่สำเร็จ กรุณาลองใหม่";
      } finally {
        this.loading = false;
      }
    },
    notify(text, color = "success") {
      this.snack = { show: true, text, color };
    },
    hasProblem(r) {
      return r.issues.some(i => i.level !== "info");
    },
    barOf(r) {
      const s = parseDate(r.effective_start);
      const e = parseDate(r.effective_end) || s;
      if (!s || !e || e < this.fyStart || s > this.fyEnd) return null;
      const a = Math.max(0, (s - this.fyStart) / DAY);
      const b = Math.min(this.fyDays, (e - this.fyStart) / DAY + 1);
      return { left: (a / this.fyDays) * 100, width: Math.max(0.6, ((b - a) / this.fyDays) * 100) };
    },
    stateMeta(s) {
      return STATE_META[s] || STATE_META.closed;
    },
    stateText(r) {
      const base = this.stateMeta(r.state).text;
      if ((r.state === "open" || r.state === "closing_soon") && r.days_to_end !== null) {
        return r.days_to_end === 0 ? "ปิดวันนี้" : `${base} · เหลือ ${r.days_to_end} วัน`;
      }
      if (r.state === "overdue_open") return `${base} ${-r.days_to_end} วัน`;
      if (r.state === "upcoming") return `อีก ${r.days_to_start} วัน`;
      return base;
    },
    actorMeta(a) {
      return ACTOR_META[a] || ACTOR_META.central;
    },
    missionLabel(m) {
      return MISSION_LABEL[m] || m;
    },
    levelColor(l) {
      return l === "error" ? "red darken-1" : l === "warning" ? "orange darken-2" : "blue-grey";
    },
    thaiDate(raw) {
      const d = parseDate(raw);
      return d ? d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "2-digit" }) : "-";
    },
    actionText(a) {
      return (
        { update_period: "แก้ไขกำหนดการ", update_meta: "แก้ไขทะเบียน", close_overdue: "ปิดที่เลยกำหนด", update_subround: "แก้ไขงวดย่อย" }[a] || a
      );
    },
    diff(h) {
      let b = {};
      let a = {};
      try {
        b = JSON.parse(h.before_json || "{}") || {};
        a = JSON.parse(h.after_json || "{}") || {};
      } catch (e) {
        return [];
      }
      return Object.keys(a)
        .filter(k => String(b[k] === undefined ? "" : b[k] === null ? "" : b[k]) !== String(a[k] === null ? "" : a[k]))
        .map(k => ({ field: k, before: b[k] === undefined || b[k] === null || b[k] === "" ? "(ว่าง)" : b[k], after: a[k] === null || a[k] === "" ? "(ว่าง)" : a[k] }));
    },

    openEdit(r) {
      this.edit = r;
      this.editTab = r.missing ? 1 : 0;
      this.history = [];
      this.pf = {
        year: r.year,
        times: Number(r.times),
        start: r.start,
        stop: r.stop,
        attach_stop: r.attach_stop || "",
        enabled: r.enabled,
        sw2: r.sw2,
        comment: r.comment || "",
        reason: ""
      };
      this.mf = {
        title: r.registered ? r.title : r.comment || "",
        description: r.description || "",
        mission: r.mission,
        actor: r.actor,
        status: r.reg_status,
        admin_route: r.admin_route || "",
        user_pages: r.user_pages || "",
        known_issue: r.known_issue || "",
        sort_order: r.sort_order === 999 ? 0 : r.sort_order
      };
      this.sf = r.subrounds.map(s => ({ ...s, comment: s.comment || "" }));
      this.editOpen = true;
    },
    async loadHistory() {
      if (!this.edit) return;
      this.historyLoading = true;
      try {
        const d = await this.api({ action: "history", source_table: this.edit.source_table, source_id: this.edit.source_id });
        this.history = d.logs || [];
      } finally {
        this.historyLoading = false;
      }
    },
    async run(payload, okText) {
      this.saving = true;
      try {
        const d = await this.api(payload);
        if (d.status) {
          this.notify(okText);
          await this.load();
          if (this.editTab === this.historyTab) this.loadHistory();
          return true;
        }
        this.notify(d.message || "บันทึกไม่สำเร็จ", "error");
      } catch (e) {
        this.notify("เกิดข้อผิดพลาดในการเชื่อมต่อ", "error");
      } finally {
        this.saving = false;
      }
      return false;
    },
    savePeriod() {
      return this.run(
        { action: "update_period", source_table: this.edit.source_table, source_id: this.edit.source_id, ...this.pf },
        "บันทึกกำหนดการเรียบร้อย"
      );
    },
    saveMeta() {
      return this.run(
        { action: "update_meta", source_table: this.edit.source_table, source_id: this.edit.source_id, ...this.mf },
        "บันทึกทะเบียนเรียบร้อย"
      );
    },
    saveSub(s) {
      return this.run(
        { action: "update_subround", pcr_id: s.pcr_id, pcr_start: s.start, pcr_stop: s.stop, pcr_enable: s.enabled, pcr_comment: s.comment },
        `บันทึกงวดที่ ${s.no} เรียบร้อย`
      );
    },
    async closeOverdue(keys) {
      const list = keys ? this.data.rows.filter(r => keys.includes(r.key)) : this.overdueRows;
      if (!list.length) return;
      const html =
        "<div style='text-align:left;max-height:260px;overflow:auto'>" +
        list.map(r => `• ${this.escape(r.title)} <small>(${r.key})</small>`).join("<br>") +
        "</div><div style='margin-top:8px'>ระบบจะปิดทั้งสวิตช์เปิดระบบและสวิตช์ที่ 2 · บันทึกในประวัติ</div>";
      const c = await Swal.fire({
        icon: "warning",
        title: `ปิด ${list.length} กำหนดการที่เลยวันสิ้นสุด?`,
        html,
        showCancelButton: true,
        confirmButtonText: "ปิดกำหนดการ",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#e53935"
      });
      if (!c.isConfirmed) return;
      this.closing = true;
      try {
        const d = await this.api({ action: "close_overdue", keys: list.map(r => r.key) });
        if (d.status) {
          this.notify(`ปิดแล้ว ${d.closed.length} รายการ`);
          await this.load();
        } else {
          this.notify(d.message || "ปิดไม่สำเร็จ", "error");
        }
      } finally {
        this.closing = false;
      }
    },
    escape(s) {
      return String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    }
  }
};
</script>

<style scoped>
.pcal {
  font-family: "Sarabun", "Noto Sans Thai", sans-serif;
}
.pcal-head,
.pcal-filter,
.pcal-card {
  border: 1px solid #e4e4e7 !important;
  border-radius: 14px !important;
}
.pcal-head-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #263238;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pcal-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1b365d;
}
.pcal-kpi {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 4px solid;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}
.pcal-kpi:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}
.pcal-kpi--active {
  box-shadow: 0 0 0 2px rgba(27, 54, 93, 0.35);
}
.pcal-kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}
.pcal-kpi-label {
  font-size: 0.78rem;
  color: #616161;
}
.pcal-tabitems {
  background: transparent !important;
}
.pcal-legend {
  font-size: 0.78rem;
  color: #546e7a;
  white-space: nowrap;
}
.pcal-legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 4px;
  vertical-align: middle;
}
/* Gantt */
.pcal-gantt {
  overflow-x: auto;
  padding: 0 12px 12px;
}
.pcal-g-row {
  display: flex;
  min-width: 900px;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
}
.pcal-g-row:hover {
  background: #f8fafc;
}
.pcal-g-headrow {
  cursor: default;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 2;
}
.pcal-g-label {
  flex: 0 0 300px;
  font-size: 0.8rem;
  padding: 5px 8px 5px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #37474f;
}
.pcal-g-track {
  position: relative;
  flex: 1;
  min-height: 26px;
}
.pcal-g-month {
  position: absolute;
  top: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #607d8b;
  text-align: center;
  padding: 4px 0;
  border-left: 1px solid #e2e8f0;
}
.pcal-g-grid {
  position: absolute;
  top: 0;
  bottom: 0;
  border-left: 1px dashed #edf0f3;
}
.pcal-g-today {
  position: absolute;
  top: 0;
  bottom: 0;
  border-left: 2px solid #b3151b;
  z-index: 1;
}
.pcal-g-bar {
  position: absolute;
  top: 6px;
  height: 14px;
  border-radius: 7px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.pcal-g-group {
  min-width: 900px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1b365d;
  background: #f1f5f9;
  padding: 6px 8px;
  margin-top: 6px;
  border-left: 3px solid #b3151b;
}
/* table */
.pcal-table >>> tbody tr {
  cursor: pointer;
}
.pcal-group-head {
  background: #f1f5f9 !important;
  font-weight: 700;
  color: #1b365d;
}
.pcal-desc {
  max-width: 520px;
  white-space: normal;
}
.pcal-code {
  font-size: 0.75rem;
  background: #eceff1;
  color: #37474f;
  padding: 0 4px;
  border-radius: 4px;
}
.pcal-code--dark {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}
/* issues */
.pcal-issue {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.pcal-issue-line {
  font-size: 0.85rem;
  color: #455a64;
  margin-top: 2px;
}
/* edit */
.pcal-edit-head {
  background: #263238;
  color: #fff;
}
.pcal-edit-title {
  font-size: 1.02rem;
  font-weight: 700;
  word-break: break-word;
}
.pcal-edit-body {
  min-height: 360px;
}
.pcal-sub {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 8px;
}
.pcal-hist {
  border-left: 3px solid #cfd8dc;
  padding: 4px 10px;
  margin-bottom: 8px;
}
.pcal-diff {
  word-break: break-all;
}
</style>
